import { useState } from 'react';
import { Button } from '@/ui/Button';
import { ConfirmDialog } from '@/ui/Dialog';
import { Field, InputSuelto, Select } from '@/ui/Field';
import { Skeleton } from '@/ui/Skeleton';
import { EstadoError } from '@/components/EstadoError';
import {
  useBorrarPregunta, useGuardarPreguntas, useQuizGuardado,
  type CursoDetalle, type PreguntaGuardada,
} from '../api';

/**
 * EditorDeQuiz — las preguntas del quiz final.
 *
 * ## Un fallo al cargar invitaba a duplicar el quiz
 *
 * Las preguntas guardadas se pedían con un `catch {}` vacío. Si la petición
 * fallaba, el panel pintaba la lista vacía —«Preguntas guardadas» no salía— y
 * debajo el formulario para agregar nuevas: **para quien lo mira, el quiz no
 * tenía preguntas**, y lo razonable era escribirlas otra vez. Al volver la
 * conexión, el quiz tenía cada pregunta dos veces.
 *
 * Aquí, si las guardadas no se pudieron leer, se dice y **no se ofrece agregar**
 * hasta que se lean: sin saber qué hay, cualquier alta puede ser un duplicado.
 *
 * ## La respuesta correcta se ve solo aquí
 *
 * `correct_key` sale del servidor únicamente por `/quiz/manage`, que exige
 * gestión. `/quiz`, el que usa quien lo presenta, nunca la trae.
 */

type Borrador = {
  question_text: string;
  correct_key: string;
  options: Array<{ key: string; text: string }>;
};

const NUEVA = (): Borrador => ({
  question_text: '',
  correct_key: 'a',
  options: [{ key: 'a', text: '' }, { key: 'b', text: '' }, { key: 'c', text: '' }],
});

export function EditorDeQuiz({ curso }: { curso: CursoDetalle }) {
  const guardadas = useQuizGuardado(curso.id, true);
  const guardar = useGuardarPreguntas();
  const borrar = useBorrarPregunta();
  const [borradores, setBorradores] = useState<Borrador[]>([]);
  const [leccionId, setLeccionId] = useState<string>(() => String(curso.lessons[0]?.id ?? ''));
  const [borrando, setBorrando] = useState<{ p: PreguntaGuardada; n: number } | null>(null);

  if (!curso.lessons.length) {
    return (
      <p className="rounded-sm border border-line bg-sunken p-3 text-3 text-ink-2">
        <i className="bi bi-info-circle mr-1.5 text-brand-ink" aria-hidden="true" />
        Agrega alguna lección antes de crear el quiz: cada pregunta pertenece a una.
      </p>
    );
  }

  function cambiar(i: number, cambio: Partial<Borrador>) {
    setBorradores((bs) => bs.map((b, j) => (j === i ? { ...b, ...cambio } : b)));
  }
  function cambiarOpcion(i: number, j: number, texto: string) {
    setBorradores((bs) =>
      bs.map((b, k) => (k === i ? { ...b, options: b.options.map((o, m) => (m === j ? { ...o, text: texto } : o)) } : b)),
    );
  }

  function guardarTodo() {
    if (!borradores.length) return;
    const incompleta = borradores.findIndex(
      (b) => !b.question_text.trim() || b.options.some((o) => !o.text.trim()),
    );
    if (incompleta >= 0) {
      Utils.toast(`La pregunta ${incompleta + 1} tiene el enunciado o alguna opción vacía`, 'warning');
      return;
    }
    // Los borradores tal como salen: si falla a mitad, se quitan POR IDENTIDAD
    // los que se guardaron. Por posición no valdría: quitar uno mientras se
    // guarda desplazaría a los demás.
    const enviados = borradores;
    guardar.mutate(
      {
        cursoId: curso.id,
        leccionId: Number(leccionId),
        preguntas: enviados.map((b) => ({
          question_text: b.question_text.trim(),
          correct_key: b.correct_key,
          options: b.options.map((o) => ({ key: o.key, text: o.text.trim() })),
        })),
      },
      {
        onSuccess: ({ hechas, fallo }) => {
          // Las guardadas salen de los borradores y las que falten se quedan,
          // para reintentar sin duplicar ninguna. Lo que se añadiera mientras
          // tanto tampoco se pierde.
          const guardados = new Set(hechas.map((i) => enviados[i]));
          setBorradores((bs) => bs.filter((b) => !guardados.has(b)));
          if (fallo) {
            const motivo = fallo instanceof Error ? fallo.message : 'error desconocido';
            Utils.toast(
              `Se guardaron ${hechas.length} de ${enviados.length}; las demás fallaron (${motivo}). ` +
                'Siguen abajo para volver a intentarlo.',
              'warning',
              8000,
            );
            return;
          }
          const n = hechas.length;
          Utils.toast(`${n} pregunta${n === 1 ? ' guardada' : 's guardadas'}`, 'success');
        },
        onError: (err) =>
          Utils.toast(err instanceof Error ? err.message : 'No se pudieron guardar las preguntas', 'error'),
      },
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <p className="rounded-sm border border-line bg-sunken p-3 text-2 text-ink-2">
        <i className="bi bi-info-circle mr-1.5 text-brand-ink" aria-hidden="true" />
        Cada pregunta pertenece a una lección. Para aprobar hace falta el mínimo que fija el
        servidor (hoy, 70 %).
      </p>

      <section aria-labelledby="quiz-guardadas">
        <h3 id="quiz-guardadas" className="mb-2 text-3 font-bold text-brand-ink">
          <i className="bi bi-check2-square" aria-hidden="true" /> Preguntas guardadas
          {guardadas.data ? ` (${guardadas.data.length})` : ''}
        </h3>
        {guardadas.isLoading ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : guardadas.isError ? (
          <EstadoError
            err={guardadas.error}
            que="las preguntas guardadas"
            onReintentar={() => void guardadas.refetch()}
          />
        ) : !guardadas.data?.length ? (
          <p className="text-3 text-muted">Todavía no hay ninguna.</p>
        ) : (
          <ol className="flex flex-col gap-2">
            {guardadas.data.map((p, i) => (
              <li key={p.id} className="flex items-start gap-2 rounded-sm border border-line bg-sunken p-2.5">
                <span className="w-5 text-center font-bold tabular-nums text-muted" aria-hidden="true">{i + 1}</span>
                <p className="min-w-0 flex-1 text-3 [overflow-wrap:anywhere]">
                  {p.question_text}
                  <span className="ml-1.5 text-2 text-ok-ink">
                    · correcta: {String(p.correct_key || '').toUpperCase()}
                  </span>
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={`Eliminar la pregunta ${i + 1}`}
                  onClick={() => setBorrando({ p, n: i + 1 })}
                >
                  <i className="bi bi-trash text-danger-ink" aria-hidden="true" />
                </Button>
              </li>
            ))}
          </ol>
        )}
      </section>

      {/* Sin saber qué hay guardado, cualquier alta puede ser un duplicado: el
          formulario espera a que la lista se haya leído bien. */}
      {guardadas.isSuccess && (
        <section aria-labelledby="quiz-nuevas" className="flex flex-col gap-3 border-t border-line pt-4">
          <h3 id="quiz-nuevas" className="text-3 font-bold text-brand-ink">
            <i className="bi bi-plus-square" aria-hidden="true" /> Agregar preguntas
          </h3>

          <Field label="Lección a la que pertenecen">
            <Select value={leccionId} onChange={(e) => setLeccionId(e.target.value)}>
              {curso.lessons.map((l) => (
                <option key={l.id} value={l.id}>{l.title}</option>
              ))}
            </Select>
          </Field>

          {borradores.map((b, i) => (
            <fieldset key={i} className="flex flex-col gap-2 rounded-sm border border-line bg-sunken p-3">
              <legend className="flex w-full items-center justify-between text-3 font-bold">
                <span>Pregunta {i + 1}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  aria-label={`Quitar la pregunta ${i + 1}`}
                  onClick={() => setBorradores((bs) => bs.filter((_, j) => j !== i))}
                >
                  <i className="bi bi-trash text-danger-ink" aria-hidden="true" />
                </Button>
              </legend>
              <InputSuelto
                aria-label={`Enunciado de la pregunta ${i + 1}`}
                autoFocus={i === borradores.length - 1}
                value={b.question_text}
                onChange={(e) => cambiar(i, { question_text: e.target.value })}
                placeholder="Escribe la pregunta…"
              />
              <p className="text-1 text-muted">Marca la respuesta correcta.</p>
              {b.options.map((o, j) => (
                <div key={o.key} className="flex items-center gap-2">
                  <input
                    type="radio"
                    id={`qz-${i}-${o.key}`}
                    name={`qz-correcta-${i}`}
                    checked={b.correct_key === o.key}
                    onChange={() => cambiar(i, { correct_key: o.key })}
                    className="size-4 shrink-0 accent-brand"
                  />
                  <label htmlFor={`qz-${i}-${o.key}`} className="sr-only">
                    La opción {o.key.toUpperCase()} es la correcta
                  </label>
                  <InputSuelto
                    className="flex-1"
                    aria-label={`Texto de la opción ${o.key.toUpperCase()}`}
                    value={o.text}
                    onChange={(e) => cambiarOpcion(i, j, e.target.value)}
                    placeholder={`Opción ${o.key.toUpperCase()}`}
                  />
                </div>
              ))}
            </fieldset>
          ))}

          <div className="flex flex-wrap justify-between gap-2">
            <Button type="button" variant="secondary" size="sm" onClick={() => setBorradores((bs) => [...bs, NUEVA()])}>
              <i className="bi bi-plus-circle" aria-hidden="true" /> Otra pregunta
            </Button>
            {borradores.length > 0 && (
              <Button type="button" onClick={guardarTodo} loading={guardar.isPending}>
                <i className="bi bi-floppy" aria-hidden="true" /> Guardar {borradores.length}{' '}
                pregunta{borradores.length === 1 ? '' : 's'}
              </Button>
            )}
          </div>
        </section>
      )}

      <ConfirmDialog
        abierto={borrando != null}
        onCambio={(v) => { if (!v) setBorrando(null); }}
        titulo="Eliminar la pregunta"
        descripcion={
          borrando
            ? `La pregunta ${borrando.n} sale del quiz. El puntaje de quien lo presente se calcula sobre las que quedan.`
            : ''
        }
        textoConfirmar="Eliminar"
        destructivo
        cargando={borrar.isPending}
        onConfirmar={() => {
          if (!borrando) return;
          borrar.mutate(
            { cursoId: curso.id, preguntaId: borrando.p.id },
            {
              onSuccess: () => { setBorrando(null); Utils.toast('Pregunta eliminada', 'info'); },
              onError: (err) => {
                setBorrando(null);
                Utils.toast(err instanceof Error ? err.message : 'No se pudo eliminar', 'error');
              },
            },
          );
        }}
      />
    </div>
  );
}
